import { useTheme } from '@mui/material';
import { CURRENCIES, DEFAULT_LOCALE, SHORT_DATE_FORMAT } from '@npi/buddy-utils';
import { ApplicationStatus } from '@prisma/client';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as yup from 'yup';

import { showErrorToast } from 'src/components/Toasts/toast';
import { BUSINESS_TYPES, CountryCodes, mimeTypes } from 'src/constants';
import { isIreland } from 'src/lib/countryConfig';
import { enumToCamelCase, stringToCamelCase } from 'src/lib/formatters';
import { Address } from 'src/types/AddMerchant/merchantInfo';

dayjs.extend(customParseFormat);

export const formatProductId = (type: string, id: number) => {
    const productType = type.slice(0, 3).toUpperCase();
    const productId = `${id}`.padStart(5, '0');
    return `${productType}${productId}`;
};

export const sequenceGenerator = ({
    start,
    stop,
    step,
}: {
    start: number;
    stop: number;
    step: number;
}) => {
    return Array.from({ length: (stop - start) / step + 1 }, (_, i) => {
        return start + i * step;
    });
};

export const formatDate = ({
    date,
    format = SHORT_DATE_FORMAT,
}: {
    date: Date;
    format?: string;
}) => {
    return dayjs(date).format(format);
};

export const addDaysToDate = ({ date, days }: { date: Date; days: number }) => {
    return dayjs(`${date}`).add(days, 'day').toDate();
};

export const formatCurrency = (value: number, currency: string, withSymbol = true) => {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
        style: withSymbol ? 'currency' : 'decimal',
        currency,
        minimumFractionDigits: 2,
    }).format(value);
};

export const calculateTimeAtAddressInYears = (years: number, months: number) => {
    const yearsToMonths = years > 0 ? years * 12 : 0;

    return (yearsToMonths + months) / 12;
};

export const getAddressValidation = (isRequired: boolean) => {
    if (isRequired) {
        return {
            eircode: yup.string().required('Required'),
            addressLine1: yup.string().required('Required'),
            addressLine2: yup.string(),
            city: yup.string().required('Required'),
            county: yup.string(),
            country: yup.string().required('Required'),
            timeAtAddress: yup.object().shape({
                years: yup.number().required('Required'),
                months: yup.number().required('Required'),
            }),
        };
    }
    return {
        eircode: yup.string(),
        addressLine1: yup.string(),
        addressLine2: yup.string(),
        city: yup.string(),
        county: yup.string(),
        country: yup.string(),
        timeAtAddress: yup.object().shape({
            years: yup.number(),
            months: yup.number(),
        }),
    };
};

export const getCurrencySymbol = (currencyValue) => {
    const selectedCurrency = CURRENCIES.find((c) => {
        return c.value === currencyValue;
    });

    if (selectedCurrency) {
        return selectedCurrency.symbol;
    }

    return '';
};

export const getCurrencyValue = (currencySymbol) => {
    const selectedCurrency = CURRENCIES.find((c) => {
        return c.symbol === currencySymbol;
    });

    if (selectedCurrency) {
        return selectedCurrency.value;
    }

    return '';
};

export const showPartnerBusinessLegalNames = (partners) => {
    if (Array.isArray(partners) && partners.length >= 1) {
        const firstElement = partners[0]?.business?.legalName;
        return partners.length > 1 ? `${firstElement}...` : firstElement;
    }
    return '';
};

export const showOfferLegacyId = (id: number, isLegacy: boolean) => {
    return isLegacy ? `LEG-${id}` : id;
};

// extracts address info from the address string returned from the backend
export const extractAddress = (addressString: string | null | undefined) => {
    if (!addressString) {
        return { addressLine1: null, country: null, town: null };
    }
    const [addressLine1, townOrCounty, county] = addressString.split(',').map((item) => {
        return item.trim();
    });

    if (county === undefined) {
        // There are only two items in the address string, so the second item is the county
        return { addressLine1, county: townOrCounty };
    }
    // There are three items in the address string, so the second item is the town
    return { addressLine1, town: townOrCounty, county };
};
/**
 * Formats an address object into a comma-separated string of address fields.
 *
 * @param {Partial<Address> & { city?: string }} address - The address object to format.
 *   Supports both 'townOrCity' and 'city' fields for the city/town value (e.g., 'townOrCity' is used in Address, 'city' in ExtendedAddress).
 *   Other fields may include addressLine1, addressLine2, county, country, and eircode.
 * @returns {string} A comma-separated string of the non-empty address fields, or an empty string if no address is provided.
 */
export const formatAddressString = (address?: Partial<Address> & { city?: string }) => {
    if (!address) return '';

    const { addressLine1, addressLine2, townOrCity, city, county, country, eircode } = address;

    // Support both 'townOrCity' and 'city' field names (city is used in ExtendedAddress)
    const cityField = townOrCity || city;

    const parts = [addressLine1, addressLine2, cityField, county, country, eircode].filter(
        (field: string | undefined) => {
            return field && field.trim() !== '';
        }
    );

    return parts.join(', ');
};

const isValidUrl = (url: string) => {
    try {
        new URL(url);
        return true;
    } catch (err) {
        return false;
    }
};

// function to download document into its right format
export const downloadDocument = async (options, getBlob) => {
    const { fileUrl, fileName, isDownloadingToBrowser } = options;

    if (!isValidUrl(fileUrl)) {
        throw new TypeError('Invalid URL');
    }

    const response = await getBlob(fileUrl);
    if (isDownloadingToBrowser) {
        // Trigger the download in the browser
        const downloadUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        return { success: true };
    }
    return { file: response.data, fileUrl };
};

export const updateStepCompletion = (steps, completionStatus) => {
    let hasTrueCompleted = false;

    return steps.map((step, index) => {
        const { accessor } = step;
        const isCompleted = completionStatus[accessor];

        // Check if any other step has `true` completed, and if not already set, set the first one to `true`.
        if (isCompleted && !hasTrueCompleted) {
            hasTrueCompleted = true;
            return { ...step, completed: true };
        }

        return {
            ...step,
            completed: accessor === 'partnerId' ? true : isCompleted,
        };
    });
};

export const findLastCompletedStepPosition = (steps): number | undefined => {
    for (let i = steps.length - 1; i >= 0; i--) {
        if (steps[i].completed === true) {
            return i;
        }
    }
    return undefined;
};

export const onError = (error: any) => {
    try {
        showErrorToast(error.message);
    } catch (error) {
        showErrorToast('Something went wrong');
    }
};

export const hasRequiredSupportingDocuments = (
    businessType,
    settlementAccountSameAsChargeable,
    supportingDocuments
) => {
    if (!isIreland) return true; // Not required for non-Ireland

    let hasAllRequiredDocuments = false;
    if (supportingDocuments) {
        const principalDocuments = supportingDocuments?.principalDocuments;
        const proofOfBank = supportingDocuments?.proofOfBank;
        const b1FormFile = supportingDocuments?.b1FormFile;
        const businessReport = supportingDocuments?.businessReportFile;
        const chargeableAccountFile = proofOfBank?.chargeableAccountFile;
        const settlementAccountFile = proofOfBank?.settlementAccountFile;
        const partnershipDeedFile = supportingDocuments?.partnershipDeedFile;
        const hasRequiredPartnershipDeed = partnershipDeedFile?.locationUrl?.length > 0;
        const hasRequiredB1Form = b1FormFile?.locationUrl?.length > 0;
        const hasRequiredBusinessReport = businessReport?.locationUrl?.length > 0;

        const hasRequiredPrincipalDocuments = principalDocuments
            .filter(({ isBeneficialOwner }) => {
                return isBeneficialOwner;
            })
            .every(({ addressFile, idFile }) => {
                return (
                    addressFile?.fileName?.length > 0 &&
                    addressFile?.locationUrl?.length > 0 &&
                    idFile?.fileName?.length > 0 &&
                    idFile?.locationUrl?.length > 0
                );
            });

        let hasRequiredBankAccountDocuments = false;

        if (settlementAccountSameAsChargeable) {
            hasRequiredBankAccountDocuments =
                (chargeableAccountFile?.fileName?.length > 0 &&
                    chargeableAccountFile?.locationUrl?.length > 0) ||
                (settlementAccountFile?.fileName?.length > 0 &&
                    settlementAccountFile?.locationUrl?.length > 0);
        } else {
            hasRequiredBankAccountDocuments =
                chargeableAccountFile?.fileName?.length > 0 &&
                settlementAccountFile?.locationUrl?.length > 0 &&
                settlementAccountFile?.fileName?.length > 0 &&
                chargeableAccountFile?.locationUrl?.length > 0;
        }

        if (businessType === BUSINESS_TYPES.SOLE_TRADER) {
            if (hasRequiredBankAccountDocuments && hasRequiredPrincipalDocuments) {
                hasAllRequiredDocuments =
                    hasRequiredBankAccountDocuments && hasRequiredPrincipalDocuments;
            }
        } else if (businessType === BUSINESS_TYPES.PARTNERSHIP) {
            hasAllRequiredDocuments =
                hasRequiredBankAccountDocuments &&
                hasRequiredPrincipalDocuments &&
                hasRequiredBusinessReport &&
                hasRequiredPartnershipDeed;
        } else if (
            businessType === BUSINESS_TYPES.PRIVATE_LIMITED_COMPANY ||
            businessType === BUSINESS_TYPES.PUBLIC_LIMITED_COMPANY ||
            businessType === BUSINESS_TYPES.LIMITED_LIABILITY_PARTNERSHIP
        ) {
            if (
                hasRequiredBankAccountDocuments &&
                hasRequiredPrincipalDocuments &&
                hasRequiredB1Form &&
                hasRequiredBusinessReport
            ) {
                hasAllRequiredDocuments =
                    hasRequiredBankAccountDocuments &&
                    hasRequiredPrincipalDocuments &&
                    hasRequiredB1Form &&
                    hasRequiredBusinessReport;
            }
        }
    }
    return hasAllRequiredDocuments;
};

export const updatePrincipalDocuments = (updatedPrincipals, merchantApplication) => {
    const { principalDocuments } = merchantApplication?.supportingDocuments ?? {};

    // Update existing principal documents with corresponding updated principals
    const updatedSupportingDocuments = {
        ...merchantApplication?.supportingDocuments,
        principalDocuments: principalDocuments
            .map((principalDocument, index) => {
                const updatedPrincipal = updatedPrincipals[index];
                if (updatedPrincipal) {
                    return {
                        ...principalDocument,
                        ...updatedPrincipal,
                    };
                }
                return principalDocument;
            })
            // eslint-disable-next-line id-length
            .filter((_: unknown, index: number) => {
                return index < updatedPrincipals.length;
            }), // Filter out removed principals
    };

    // Add new principals that are not found in the existing principalDocuments
    if (updatedPrincipals.length > principalDocuments.length) {
        updatedSupportingDocuments.principalDocuments.push(
            ...updatedPrincipals.slice(principalDocuments.length)
        );
    }

    return updatedSupportingDocuments;
};

export const getEnumDropDownOptions = (enumValue) => {
    return Object.values(enumValue).map((value: string) => {
        return {
            label: value.replace(/_/g, ' '),
            value,
        };
    });
};

export const formatEnumLabel = (value) => {
    // Convert the value to a readable label by replacing underscores with spaces and capitalizing words
    return value
        .split('_')
        .map((word) => {
            return word.charAt(0) + word.slice(1).toLowerCase();
        })
        .join(' ');
};
export const mergeMimeTypes = (...types) => {
    return types.reduce((acc, type) => {
        return { ...acc, ...mimeTypes[type] };
    }, {});
};

export function getFileNameFromDownloadLink(downloadLink: string) {
    // https://npi-dev-bucket.s3.eu-west-1.amazonaws.com/1-Terminal-commission-statement%28TERRIFIC%20TEA%20LIMITED%29?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&
    // gets: 1-Terminal-commission-statement%28TERRIFIC%20TEA%20LIMITED%29
    const startIndex = downloadLink.indexOf('.com') + 5;
    const endIndex = downloadLink.indexOf('?');
    return decodeURI(downloadLink.slice(startIndex, endIndex));
}
export function parseExpiryFromAWSUrl(url) {
    try {
        const parsedUrl = new URL(url);

        const urlParams = parsedUrl.searchParams;

        const amzDate = urlParams.get('X-Amz-Date');
        const amzExpires = urlParams.get('X-Amz-Expires');

        if (!amzDate || !amzExpires) {
            throw new Error('Invalid URL or missing required parameters');
        }

        const year = Number(amzDate.substring(0, 4));
        const month = Number(amzDate.substring(4, 6)) - 1;
        const day = Number(amzDate.substring(6, 8));
        const hours = Number(amzDate.substring(9, 11));
        const minutes = Number(amzDate.substring(11, 13));
        const seconds = Number(amzDate.substring(13, 15));
        const signedDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));

        const expiryTime = new Date(signedDate.getTime() + Number(amzExpires) * 1000);

        return expiryTime;
    } catch (error) {
        throw new Error('Failed to parse the AWS URL.');
    }
}

export enum PromoType {
    THREE_MONTHS_FREE = 'THREE_MONTHS_FREE',
    SIX_MONTHS_HALF_PRICE = 'SIX_MONTHS_HALF_PRICE',
}
export const getContractTermInMonths = ({
    promoType,
    contractTermInMonths,
}: {
    promoType?: PromoType;
    contractTermInMonths: number;
}) => {
    if (
        promoType === PromoType.THREE_MONTHS_FREE ||
        promoType === PromoType.SIX_MONTHS_HALF_PRICE
    ) {
        return contractTermInMonths + 3;
    }
    return contractTermInMonths;
};

export const getMinimumContractTerm = (serviceRequirements) => {
    const { service, productOrder, customTerm } = serviceRequirements;
    let productOrderTerm = productOrder?.term || 0;

    if (productOrderTerm === 0 && customTerm) {
        productOrderTerm = customTerm;
    }
    const promoPeriod = service?.promoOffer;
    const promoType =
        promoPeriod === 3
            ? PromoType.THREE_MONTHS_FREE
            : promoPeriod === 6
              ? PromoType.SIX_MONTHS_HALF_PRICE
              : undefined;

    const contractTermInMonths = getContractTermInMonths({
        promoType,
        contractTermInMonths: productOrderTerm,
    });
    return contractTermInMonths;
};

export function hasMoreThan2DecimalValues(value: number) {
    const formattedNumber = new Intl.NumberFormat('en-GB').formatToParts(value);

    const fractionPart = formattedNumber?.find((part) => {
        return part.type === 'fraction';
    })?.value;

    const amountOfDecimals = fractionPart?.length;

    return amountOfDecimals > 2;
}

export const getStatusStyles = (status: string) => {
    const theme = useTheme();
    const convertedStatus = stringToCamelCase(status); // For cases when the status is received as a Uppercase string
    switch (convertedStatus) {
        case enumToCamelCase(ApplicationStatus.DRAFT):
            return {
                borderColor: theme.palette.dark[800],
                backgroundColor: 'rgba(153, 153, 153, 0.38)',
                color: theme.palette.dark[800],
            };
        case enumToCamelCase(ApplicationStatus.AWAITING_SIGNATURE):
            return {
                borderColor: theme.palette.orange.main,
                backgroundColor: 'rgba(241, 143, 52, 0.29)',
                color: theme.palette.orange.main,
            };
        case enumToCamelCase(ApplicationStatus.AWAITING_SUPPORTING_DOCUMENTS):
        case enumToCamelCase(ApplicationStatus.NPI_INTERNAL_CS_REVIEW):
            return {
                borderColor: theme.palette.orange.main,
                backgroundColor: 'rgba(253, 200, 0, 0.4)',
                color: theme.palette.orange.main,
            };
        case enumToCamelCase(ApplicationStatus.ERROR_IDENTIFIED):
        case enumToCamelCase(ApplicationStatus.EXPIRED):
        case enumToCamelCase(ApplicationStatus.CANCELLED):
            return {
                borderColor: theme.palette.warning.medium,
                backgroundColor: 'rgba(199, 21, 21, 0.4)',
                color: theme.palette.warning.medium,
            };
        case enumToCamelCase(ApplicationStatus.ACTIVE):
        case enumToCamelCase(ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID):
            return {
                borderColor: theme.palette.blue.light,
                backgroundColor: 'rgba(39, 174, 150, 0.23)',
                color: theme.palette.blue.light,
            };
        case enumToCamelCase(ApplicationStatus.APPLICATION_ACCEPTED):
        case enumToCamelCase(ApplicationStatus.AWAITING_HARDWARE_DISPATCH):
            return {
                borderColor: theme.palette.blue.dark,
                backgroundColor: 'rgba(16, 76, 229, 0.37)',
                color: theme.palette.blue.dark,
            };
        default:
            return {};
    }
};
export const stripStringSpaces = (text: string) => {
    return text.replace(/\s+/g, '').toLowerCase();
};
export const hasInvalidChars = (value: string) => {
    return /[^a-zA-Z0-9]/.test(value);
};

export function supportedCountriesForIreland(): CountryCodes[] {
    return ['IE'];
}

export function supportedCountriesForGreatBritain(): CountryCodes[] {
    return ['GB', 'XI'];
}

export function supportedCountries(isIreland: boolean): CountryCodes[] {
    if (isIreland) {
        return supportedCountriesForIreland();
    }
    return supportedCountriesForGreatBritain();
}

/**
 * Gets the nested property value from an object based on a given path separated by .
 * e.g. { my: { nested: { property: 'value' }}} and 'my.nested.property' will yield 'value'
 * @param obj The object that contains the properties at the path
 * @param path The path to the property
 * @returns
 */
export function getDescendantProp<T extends Record<string, unknown>>(
    obj: T,
    path: string
): unknown {
    return path.split('.').reduce((acc: unknown, part: string) => {
        return acc && typeof acc === 'object' && acc !== null
            ? (acc as Record<string, unknown>)[part]
            : undefined;
    }, obj);
}

export const getCurrencyCodeBasedOnRegion = () => {
    let currencyCode;
    if (isIreland) {
        currencyCode = 'EUR';
    } else {
        currencyCode = 'GBP';
    }
    return currencyCode;
};
